import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import { checkData, deleteItem, updateData } from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  departmentsTooltip,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";

const YES_NO = ["Yes", "No"];

const SpaItemNumbersForAddOns = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [itemNumber, setItemNumber] = useState<number | "">("");
  const [addOnName, setAddOnName] = useState<string | null>(null);
  const [addOnDescription, setAddOnDescription] = useState<string | null>(null);
  const [taxable, setTaxable] = useState<string>("No");
  const [active, setActive] = useState<string>("Yes");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [nameError, setNameError] = useState<boolean>(false);

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const updateItem = () => {
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage("Item Numbers for Add Ons updated successfully");
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (itemNumber === "" || itemNumber === undefined) {
      setAlertMessage("Item Number is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setItemNumber(
        list["Item Number"] !== undefined && list["Item Number"] !== null
          ? Number(list["Item Number"])
          : "",
      );
      setAddOnName(list["Add On Name"] ?? null);
      setAddOnDescription(list["Add On Description"] ?? null);
      setTaxable(list?.Taxable ?? "No");
      setActive(list?.Active ?? "Yes");
    } else {
      setItemNumber("");
      setAddOnName(null);
      setAddOnDescription(null);
      setTaxable("No");
      setActive("Yes");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Item Number": itemNumber === "" ? undefined : Number(itemNumber),
    "Add On Name": addOnName,
    "Add On Description": addOnDescription,
    Taxable: taxable,
    Active: active,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Item Numbers for Add Ons
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.addOnItemNumberHeader ??
                  "Item Numbers for Add Ons",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack
          className="form-checkbox-row form-two-col-row"
          direction="row"
          width="100%"
          gap="20px"
          flexWrap="wrap"
        >
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Item Number *</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.addOnItemNumberItemNumber,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              type="number"
              variant="outlined"
              size="small"
              value={itemNumber}
              onChange={(e) =>
                setItemNumber(
                  e.target.value === ""
                    ? ""
                    : parseInt(e.target.value, 10) || "",
                )
              }
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Add On Name </FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.addOnServiceAddOnName,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              size="small"
              value={addOnName ?? ""}
              onChange={(e) => setAddOnName(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Add On Description</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.addOnItemNumberItemDescription,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              size="small"
              value={addOnDescription ?? ""}
              onChange={(e) => setAddOnDescription(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Taxable</FormLabel>
              <Tooltip
                title={tooltipComponent(departmentsTooltip.departmentName)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Autocomplete
              size="small"
              options={YES_NO}
              value={taxable}
              onChange={(_, v) => setTaxable(v ?? "No")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" />
              )}
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Active</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.addOnItemNumberActive,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Autocomplete
              size="small"
              options={YES_NO}
              value={active}
              onChange={(_, v) => setActive(v ?? "Yes")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Item
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">
            Item Numbers for Add Ons Available
          </Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.ITEMNUMBER}
              name="Add On Name"
            />
          ) : (
            <Typography variant="body2">
              No Item Numbers for Add Ons Available
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaItemNumbersForAddOns;
